<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import appConfig from "../../../app.config";
import axios from 'axios';
import Swal from 'sweetalert2';
import { usePermissions } from '@/composables/usePermissions';

export default {
  page: {
    title: "Bank Statement Import",
    meta: [{ name: "description", content: appConfig.description }],
  },
  data() {
    return {
      title: "Bank Statement Upload",
      items: [
        { text: "Payment", href: "/" },
        { text: "Bank Statement Import", active: true },
      ],
      selectedFile: null,
      fileName: '',
      uploading: false,
      uploadProgress: 0,
    };
  },
  setup() {
    const { canCreatePayment } = usePermissions(); // Adjust permission name as needed
    return { canCreatePayment };
  },
  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type (PDF, Excel, CSV)
      const allowedTypes = [
        'application/pdf',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];

      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          title: 'Invalid File',
          text: 'Please upload a valid bank statement (PDF, Excel, or CSV)',
          icon: 'error'
        });
        return;
      }

      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        Swal.fire({
          title: 'File Too Large',
          text: 'Maximum file size is 10MB',
          icon: 'error'
        });
        return;
      }

      this.selectedFile = file;
      this.fileName = file.name;
    },

    async uploadBankStatement() {
      if (!this.selectedFile) {
        Swal.fire({
          title: 'No File Selected',
          text: 'Please select a bank statement file',
          icon: 'warning'
        });
        return;
      }

      this.uploading = true;
      this.uploadProgress = 0;

      const formData = new FormData();
      formData.append('file', this.selectedFile);

      try {
        axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
        
        const response = await axios.post(
          process.env.VUE_APP_API_BASEURL + 'payment/bank-statements/imports',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
          }
        );

        Swal.fire({
          title: 'Success',
          text: response.data.message || 'Bank statement imported successfully',
          icon: 'success',
          confirmButtonText: 'OK'
        });

        // Reset form
        this.selectedFile = null;
        this.fileName = '';
        this.uploadProgress = 0;

      } catch (error) {
        Swal.fire({
          title: 'Upload Failed',
          text: error.response?.data?.message || 'Failed to process bank statement',
          icon: 'error'
        });
      } finally {
        this.uploading = false;
      }
    },

    removeFile() {
      this.selectedFile = null;
      this.fileName = '';
      this.uploadProgress = 0;
    }
  },
  components: { Layout, PageHeader }
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row justify-content-center">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">Upload Bank Statement</h5>
          </div>
          <div class="card-body">
            <div class="text-center py-4">
              <i class="ri-upload-cloud-2-line display-1 text-primary mb-3"></i>
              <h5>Upload Bank Statement File</h5>
              <p class="text-muted">Supported formats: Excel (.xls, .xlsx), CSV</p>
            </div>

            <!-- File Upload Area -->
            <div 
              class="border border-dashed rounded p-5 text-center mb-4"
              :class="{ 'bg-light': !fileName }"
              @drop.prevent="handleDrop"
              @dragover.prevent
            >
              <input 
                type="file" 
                id="bankStatementFile"
                class="d-none"
                accept=".pdf,.xls,.xlsx,.csv"
                @change="handleFileSelect"
              >
              
              <label 
                for="bankStatementFile" 
                class="btn btn-outline-primary px-4"
                style="cursor: pointer;"
              >
                <i class="ri-upload-2-line me-2"></i>
                Choose File
              </label>

              <div v-if="fileName" class="mt-4">
                <div class="d-flex align-items-center justify-content-center gap-3">
                  <i class="ri-file-line fs-3 text-success"></i>
                  <div>
                    <strong>{{ fileName }}</strong>
                    <button 
                      @click="removeFile" 
                      class="btn btn-sm btn-link text-danger ms-3"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Progress Bar -->
            <div v-if="uploading" class="mb-4">
              <div class="progress">
                <div 
                  class="progress-bar progress-bar-striped progress-bar-animated" 
                  :style="{ width: uploadProgress + '%' }"
                >
                  {{ uploadProgress }}%
                </div>
              </div>
              <small class="text-muted">Uploading file...</small>
            </div>

            <div class="d-grid">
              <button 
                class="btn btn-success btn-lg" 
                :disabled="uploading || !selectedFile"
                @click="uploadBankStatement"
              >
                <i v-if="uploading" class="ri-loader-4-line spinner-border-sm me-2"></i>
                {{ uploading ? 'Processing...' : 'Upload & Import' }}
              </button>
            </div>
          </div>
        </div>

        <div class="text-center mt-3">
          <small class="text-muted">
            Maximum file size: 10MB • Supported: PDF, Excel, CSV
          </small>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.border-dashed {
  border-style: dashed !important;
  transition: all 0.3s ease;
}

.border-dashed:hover {
  background-color: #f8f9fa;
  border-color: #0d6efd;
}
</style>